import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { css } from "@emotion/react";
import { useGetMediaSize } from "../../hooks";

interface BlogPost {
  title: string;
  body: string;
  summary: string;
  slug: string;
  id: string;
  short_url: string;
  date: string;
  timestamp: number;
}
const blogIdentifier = "kuchinai-sakura.tumblr.com";
const apiKey = "n2Or2puPy2EYcgD3sjwpkAzikw5yIbrM7Jsa8HYLT6pD3BST3f";

const TumblrBlogPosts: React.FC = () => {
  const { width } = useGetMediaSize({});
  const [posts, setPosts] = useState<BlogPost[]>([]);

  const getImgSrc = useCallback((body: string) => {
    // imgタグのsrc属性のURLを抜き出す正規表現
    const regex = /<img.*?src="(.*?)"/;
    const match = body.match(regex);

    if (match && match[1]) {
      return match[1];
    }
  }, []);

  const getDate = useCallback((dateString: string) => {
    const regex = /(\d{4}-\d{2}-\d{2})/;
    const match = dateString.match(regex);

    if (match) {
      return match[1]; // 出力: YYYY-MM-DD hh:mm
    }
  }, []);

  const handleConvert = useCallback((seconds?: number) => {
    if (!seconds) return;
    // 日本時間に変換 (JSTはUTC+9)
    const date = new Date(seconds * 1000);
    date.setHours(date.getHours() + 9);
    const year = date.getFullYear();
    const _month = date.getMonth() + 1;
    const month = _month < 10 ? `0${_month}` : _month;
    const _day = date.getDate();
    const day = _day < 10 ? `0${_day}` : _day;
    return `${year}-${month}-${day}`;
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      // Tumblr APIのエンドポイントとブログの識別子を使用
      const response = await axios.get(
        `https://api.tumblr.com/v2/blog/${blogIdentifier}/posts?api_key=${apiKey}`,
      );

      const posts = response.data.response.posts;
      setPosts(posts);
    };
    fetchPosts();
  }, []);

  return (
    <div css={mainStyle(width)}>
      {posts.map((post, index) => {
        const src = getImgSrc(post.body);
        const date = getDate(post.date);
        // 最終要素のupdatedを取得
        const updatedDate = handleConvert(post.timestamp);
        return (
          <div key={index} css={postItemContainerStyle(index)}>
            <a
              href={post.short_url}
              target="_blank"
              rel="noopener"
              css={linkStyle}
            >
              <div css={postItemWrapperStyle}>
                {src && (
                  <div css={imgWrapperStyle(width / 7, width / 3)}>
                    <img src={src} css={imgStyle} />
                  </div>
                )}
                <div css={contentsWrapperStyle}>
                  <div css={titleStyle}>{post.summary}</div>
                  {post.slug.length > 0 && (
                    <div css={slugStyle}>{post.slug}...</div>
                  )}
                  {updatedDate && <div css={dateStyle}>{updatedDate}</div>}
                </div>
              </div>
            </a>
          </div>
        );
      })}
    </div>
  );
};

const spSize = 768;

const mainStyle = (width: number) => css`
  width: ${width}px;
  max-height: 400px;
  margin-top: 40px;
  padding: 30px;
  background-color: rgba(255, 255, 255, 0.4);
  display: flex;
  flex-direction: column;
  align-items: start;
  box-sizing: border-box;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 7px;
  }
  &::-webkit-scrollbar-track {
    background-color: rgba(255, 255, 255, 1);
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(5, 39, 51, 0.9);
  }
  scrollbar-width: 7px;
  scrollbar-color: rgba(5, 39, 51, 0.9) rgba(255, 255, 255, 1);

  @media (max-width: ${spSize}px) {
    margin-top: 30px;
    padding: 20px 10px;
    max-height: 300px;
  }
`;

// 子要素の色と下線を変更しないためのスタイル
const linkStyle = css`
  color: inherit;
  text-decoration: none;
`;

const postItemContainerStyle = (idx: number) => css`
  width: 100%;
  margin-top: ${idx > 0 ? 20 : 0}px;
  padding-bottom: 15px;
  border-bottom: 1px solid #fff;
`;

const postItemWrapperStyle = css`
  width: 100%;
  display: flex;
`;

const imgWrapperStyle = (width: number, spWidth: number) => css`
  width: ${width}px;
  height: ${width}px;
  min-width: ${width}px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media (max-width: ${spSize}px) {
    width: ${spWidth}px;
    height: ${spWidth}px;
    min-width: ${spWidth}px;
  }
`;

const imgStyle = css`
  width: 100%;
  height: auto;
`;

const contentsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 10px;
`;

const titleStyle = css`
  font-size: 18px;
  color: rgba(5, 39, 51, 0.9);
  font-weight: 600;

  @media (max-width: ${spSize}px) {
    font-size: 16px;
  }
`;

const slugStyle = css`
  padding-top: 6px;
  text-align: left;

  @media (max-width: ${spSize}px) {
    font-size: 12px;
  }
`;

const dateStyle = css`
  padding-top: 6px;
  font-size: 12px;

  @media (max-width: ${spSize}px) {
    font-size: 10px;
  }
`;

export default TumblrBlogPosts;
